import React, { FC, useCallback, useContext, useMemo } from 'react';
import { APIPill } from '@fullstory/library-ui';
import { PageAPITooltipContent } from '../PageAPITooltipContent';
import { FormattedMessage, useIntl } from 'react-intl';
import { Box, Divider, Flex, IconButton, Pane, Text, TextInput } from '@fullstory/components';
import { Platform } from '@fullstory/proto/fs/api/pages';
import { NativePlatform } from '@fullstory/proto/fs/api/segment';
import { platformToLabel } from '@fullstory/util/pages';
import {
  ActiveIndexContext,
  AppearancesContext,
  appearancesProperty,
  DefaultMode,
  MultiDimensionOverview,
} from '@fullstory/signal-studio-context';
import { uniq } from 'lodash';
import css from './index.module.scss';
import classNames from 'classnames';

const webAppearancesPropertyOptions: appearancesProperty[] = [
  'path',
  'host',
  'url',
  'device',
  'queryParam',
];
const mobileAppearancesPropertyOptions: appearancesProperty[] = ['screen', 'device'];

interface APIDefinitionProps {
  idx: number;
  pageName: string;
  setPageName?: (name: string) => void; // if set, page name is editable
  platforms?: Platform[];
  onRemove?: () => void; // if provided and editable, a remove button will be shown
}

export const PageAPIDefinition: FC<APIDefinitionProps> = ({
  idx,
  pageName,
  platforms,
  setPageName,
  onRemove,
}) => {
  const intl = useIntl();
  const appearances = useContext(AppearancesContext);
  const { Appearances } = { ...appearances };
  const editable = !!setPageName;

  const activeIndex = useContext(ActiveIndexContext);
  const active = activeIndex?.index == idx;
  const setActive = useCallback(() => activeIndex?.setIndex(idx), [activeIndex, idx]);

  const event = useMemo(
    () =>
      editable
        ? null
        : {
            primary: { visitedPage: { any: {} } },
            deps: [{ pageName: { equals: { value: [pageName] } } }],
          },
    [editable, pageName],
  );
  const platformText = platforms
    ? intl.formatList(
        platforms.map(p => platformToLabel[p.platform]).sort((a, b) => a.localeCompare(b)),
        { type: 'conjunction', style: 'narrow' },
      )
    : '';

  const appearancesProps = useMemo(() => {
    const hasWebPlatform = platforms?.some(p => p.platform === NativePlatform.NATIVE_PLATFORM_WEB);
    const hasMobilePlatform = platforms?.some(
      p =>
        p.platform === NativePlatform.NATIVE_PLATFORM_IOS ||
        p.platform === NativePlatform.NATIVE_PLATFORM_ANDROID,
    );

    const allPropertyOptions: appearancesProperty[] = [];
    if (hasWebPlatform) {
      allPropertyOptions.push(...webAppearancesPropertyOptions);
    }
    if (hasMobilePlatform) {
      allPropertyOptions.push(...mobileAppearancesPropertyOptions);
    }
    const propertyOptions = uniq(allPropertyOptions);

    // only in the case of web only api pages do we show the url specific appearances
    const multiDimensionOverview: MultiDimensionOverview | undefined =
      hasWebPlatform && !hasMobilePlatform
        ? {
            propertyName: 'URL paths',
            subPropertyName: 'domains',
            overviewProperty: 'path',
            overviewSubProperty: 'host',
          }
        : undefined;

    const forceDefaultMode =
      hasWebPlatform === hasMobilePlatform
        ? // when both or neither platforms are present, show the total only for simplicity
          DefaultMode.TOTAL_ONLY
        : // when only mobile is present, show the screens
        hasMobilePlatform
        ? DefaultMode.SCREENS
        : undefined;

    return {
      propertyOptions,
      multiDimensionOverview,
      forceDefaultMode,
    };
  }, [platforms]);

  return (
    <Pane
      borderRadius={4}
      border="standard"
      onClick={setActive}
      className={classNames(css.apiLabel, { [css.active]: active })}
    >
      <Box padding={8} display="flex" flexDirection="column" gap={8}>
        {!editable && (
          <>
            <Flex justifyContent="space-between">
              <Text appearance="bodyMediumBold">
                <FormattedMessage defaultMessage="Source" id="aH4De2" />
              </Text>
              <Flex>
                <Text appearance="bodyMedium">
                  <FormattedMessage
                    defaultMessage="This page was created with the Fullstory API"
                    id="QcbQHd"
                  />
                </Text>
                <Box marginLeft={8}>
                  <APIPill tooltipContent={<PageAPITooltipContent />} />
                </Box>
              </Flex>
            </Flex>
            <Divider />
            <Flex justifyContent="space-between">
              <Text appearance="bodyMediumBold">
                <FormattedMessage
                  defaultMessage="{count, plural, one {Platform} other {Platforms}}"
                  values={{ count: platforms?.length || 1 }}
                  id="Hvr/iA"
                />
              </Text>
              <Text appearance="bodyMedium">{platformText}</Text>
            </Flex>
            <Divider />
          </>
        )}
        <Flex justifyContent="space-between" gap={8}>
          <Box flexShrink={0}>
            <Text appearance="bodyMediumBold">
              <FormattedMessage defaultMessage="API Page Name" id="IwYoTb" />
            </Text>
          </Box>
          {!editable ? (
            <Text appearance="bodyMedium">{pageName}</Text>
          ) : (
            <>
              <TextInput
                value={pageName}
                onChange={e => setPageName?.(e.target.value)}
                onFocus={setActive}
              />
              {onRemove && (
                <IconButton
                  title={intl.formatMessage({ defaultMessage: 'Remove page name', id: 'BGYN6K' })}
                  iconName="close"
                  onClick={onRemove}
                />
              )}
            </>
          )}
        </Flex>
        <Divider />
        <Box>
          <PageAPITooltipContent color="secondary" />
        </Box>
      </Box>
      {event && Appearances && (
        <Appearances
          event={event}
          propertyOptions={appearancesProps.propertyOptions}
          objectType={intl.formatMessage({ defaultMessage: 'page name', id: 'J2SKVU' })}
          multiDimensionOverview={appearancesProps.multiDimensionOverview}
          forceDefaultMode={appearancesProps.forceDefaultMode}
        />
      )}
    </Pane>
  );
};
